import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Shield, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_REDIRECT = '/home';

const DEFAULT_CONFIG = {
    background_url: 'https://images.unsplash.com/photo-1582161095006-7c4edf743cd9?w=1920&q=80',
    icon_name: 'Shield',
    title: 'PROTECCION INDUSTRIAL',
    subtitle: 'Minera Los Pelambres',
    description: 'Gestion de guias de despacho, solicitudes de permisos y control operacional.',
    accent_color: '#22D3EE',
    overlay_from: '#0A1929',
    overlay_via: '#0A1929',
    card_title: 'Acceso al Sistema',
    card_description: 'Ingrese sus credenciales para continuar',
    footer_text: 'Minera Los Pelambres - Proteccion Industrial',
};

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, isAuthenticated, user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [cfg, setCfg] = useState(DEFAULT_CONFIG);

    useEffect(() => {
        if (isAuthenticated && user) {
            navigate(DEFAULT_REDIRECT);
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        publicAPI.getLoginConfig()
            .then(res => setCfg({ ...DEFAULT_CONFIG, ...res.data }))
            .catch(() => {});
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userData = await login(loginEmail, loginPassword);
            toast.success(`Bienvenido, ${userData.nombre}`);
            navigate(DEFAULT_REDIRECT);
        } catch (err) {
            toast.error(err.message || 'Error al iniciar sesion');
        } finally {
            setLoading(false);
        }
    };

    const resolveUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `${BACKEND_URL}${url}`;
    };

    const bgUrl = resolveUrl(cfg.background_url);

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left side - Hero Image */}
            <div
                className="hidden lg:flex flex-col justify-end p-8 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${bgUrl})` }}
            >
                <div
                    className="absolute inset-0"
                    style={{
                        background: `linear-gradient(to top, ${cfg.overlay_from}E6, ${cfg.overlay_via}66, transparent)`,
                    }}
                />
                <div className="relative z-10 text-white">
                    <div className="flex items-center gap-3 mb-4">
                        <Shield className="h-10 w-10" style={{ color: cfg.accent_color }} />
                        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
                            {cfg.title}
                        </h1>
                    </div>
                    <p className="text-xl font-medium text-white/90 max-w-md">
                        {cfg.subtitle}
                    </p>
                    <p className="text-white/60 mt-2 max-w-md">
                        {cfg.description}
                    </p>
                </div>
            </div>

            {/* Right side - Auth Forms */}
            <div className="flex items-center justify-center p-4 md:p-8 bg-background">
                <div className="w-full max-w-md space-y-6">
                    {/* Mobile Logo */}
                    <div className="lg:hidden text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <Shield className="h-8 w-8 text-primary" />
                            <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-primary">
                                {cfg.title}
                            </h1>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            {cfg.subtitle}
                        </p>
                    </div>

                    <Card className="border-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.08)]">
                        <CardHeader className="space-y-1">
                            <CardTitle className="font-heading text-2xl font-bold uppercase tracking-tight">
                                {cfg.card_title}
                            </CardTitle>
                            <CardDescription>
                                {cfg.card_description}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="login-email" className="input-label">Email</Label>
                                    <Input
                                        id="login-email"
                                        type="email"
                                        placeholder="usuario@mlp.cl"
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="login-password" className="input-label">Contraseña</Label>
                                    <Input
                                        id="login-password"
                                        type="password"
                                        placeholder="••••••"
                                        value={loginPassword}
                                        onChange={(e) => setLoginPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full font-bold uppercase tracking-wider"
                                    disabled={loading}
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                            Iniciando sesion...
                                        </>
                                    ) : (
                                        <>
                                            <LogIn className="h-5 w-5 mr-2" />
                                            Iniciar Sesion
                                        </>
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <p className="text-center text-muted-foreground text-xs">
                        {cfg.footer_text}
                    </p>
                </div>
            </div>
        </div>
    );
}
